import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT, MULTIPLE, TEXT, YES_NO,
} from "const"
import {
    CcLibraryItem,
    CcLibraryItemCreate,
    CcLibraryHeading,
} from "server/models"
import { ccCompliantItemsRoute } from "client/parts/routes"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    CcLibraryItemValidation,
    LangType,

    SaveCcLibraryItem,
    GetMaxCcLibraryItems,
    IsLockedCcLibrarySet,
    SearchCcLibraryItems,
    SearchCcLibraryHeadings,

    closeModal,
    setCcLibraryItemEdit,
    setCcLibraryItemReloadList,
    setCcLibraryItemReloadView,
    setCcLibraryItemReloadEdit,
    setCcLibraryItemAvailableParentsFiltered,
    setCcLibraryHeadingAvailableParentsFiltered,
    setCcLibraryItemAvailableParentSearch,
    setCcLibraryHeadingAvailableParentSearch,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    DivInput,
    Textarea,
    UserIcon,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    SelectDropdownSearch,
    Empty,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditCompliantItemProps {
    item: CcLibraryItem
}

let isMounted: boolean

const Form = ({
    item,
}: EditCompliantItemProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)

    const baseRoute = useSelector((state: StoreType) => state.ccLibrarySet.baseRoute)
    const set = useSelector((state: StoreType) => state.ccLibrarySet.setView)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const accountList = useSelector((state: StoreType) => state.account.accountList)

    const parentHeadingId = useSelector((state: StoreType) => state.ccLibraryItem.parentHeadingId)
    const parentItemId = useSelector((state: StoreType) => state.ccLibraryItem.parentItemId)

    const itemList = useSelector((state: StoreType) => state.ccLibraryItem.itemList)
    const parentItems = useSelector((state: StoreType) => state.ccLibraryItem.availableParents)
    const parentItemsFiltered = useSelector((state: StoreType) => state.ccLibraryItem.availableParentsFiltered)
    const parentItemSearch = useSelector((state: StoreType) => state.ccLibraryItem.availableParentSearch)

    const headingList = useSelector((state: StoreType) => state.ccLibraryHeading.headingList)
    const parentHeadings = useSelector((state: StoreType) => state.ccLibraryHeading.availableParents)
    const parentHeadingsFiltered = useSelector((state: StoreType) => state.ccLibraryHeading.availableParentsFiltered)
    const parentHeadingSearch = useSelector((state: StoreType) => state.ccLibraryHeading.availableParentSearch)


    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [publicId, setPublicId] = useInput(item.publicId)
    const [compliantItem, setCompliantItem] = useInput(item.compliantItem)
    const [interpretation, setInterpretation] = useInput(item.interpretation)
    const [original, setOriginal] = useInput(item.original)
    const [note, setNote] = useInput(item.note)
    const [options, setOptions] = useInput(item.options)
    const [group, setGroup] = useInput(item.group)

    const [parentHeading, setParentHeading] = useState<CcLibraryHeading>(item.id
        ? headingList[item.headingId]
        : headingList[parentHeadingId])
    const [parentItem, setParentItem] = useState<CcLibraryItem>(item.id
        ? itemList[item.itemId]
        : itemList[parentItemId])

    const [answerType, setAnswerType] = useState(item.answerType)
    const [validation, setValidation] = useState<CcLibraryItemValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    const setViewRoute = `${baseRoute}/${VIEW}/${set.id}`

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const filtered = SearchCcLibraryItems(parentItems, parentItemSearch)
        dispatch(setCcLibraryItemAvailableParentsFiltered(filtered))
    }, [parentItems, parentItemSearch])

    useEffect(() => {
        const filtered = SearchCcLibraryHeadings(parentHeadings, parentHeadingSearch)
        dispatch(setCcLibraryHeadingAvailableParentsFiltered(filtered))
    }, [parentHeadings, parentHeadingSearch])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = item.id
            ? `${setViewRoute}${ccCompliantItemsRoute}/${VIEW}/${item.id}`
            : setViewRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newHeadingId = parentHeading ? parentHeading.id : null
        const newItemId = parentItem ? parentItem.id : null

        // new item data
        const newItemData: CcLibraryItemCreate = {
            headingId: newHeadingId,
            itemId: newItemId,
            librarySetId: set.id,
            accountId: item.id ? item.accountId : userData.accountId,
            projectId: item.id ? item.projectId : project.id,
            publicId,
            compliantItem,
            interpretation,
            original,
            note,
            creator: item.id ? item.creator : userData.userId,
            answerType,
            options: answerType === MULTIPLE ? options : "",
            order: item.id && item.headingId === newHeadingId && item.itemId === newItemId ? item.order : null,
            group,
        }

        const maxItemNumber = GetMaxCcLibraryItems(packageSettings, userData, project.id)
        SaveCcLibraryItem(
            item.id,
            newItemData,
            maxItemNumber,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newItemId) replaceHistoryState(`${setViewRoute}${ccCompliantItemsRoute}/${EDIT}/${res.newItemId}`)
            }
            if (res.validation) return

            dispatch(setCcLibraryItemEdit({
                ...newItemData,
                id: res.newItemId || item.id,
                order: res.newOrder,
            }))

            dispatch(setCcLibraryItemReloadList(false))
            dispatch(setCcLibraryItemReloadView(false))
            setTimeout(() => {
                dispatch(setCcLibraryItemReloadList(true))
                dispatch(setCcLibraryItemReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setCcLibraryItemReloadEdit(false))
                setTimeout(() => {
                    dispatch(setCcLibraryItemReloadEdit(true))
                }, 0)
            }
        })
    }

    const isLocked = IsLockedCcLibrarySet(project.id, set, userData)
    const creator = item.creator && userList[item.creator]
    const ownerAccount = item.accountId && accountList[item.accountId]

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.publicId}</Label>
                        <Input type="text" value={publicId} onChange={setPublicId} readOnly={isLocked} />
                        <Feedback>{validation && validation.publicId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.compliantItem}</Label>
                        <Textarea value={compliantItem} onChange={setCompliantItem} readOnly={isLocked} />
                        <Feedback>{validation && validation.compliantItem}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.interpretation}</Label>
                        <Textarea value={interpretation} onChange={setInterpretation} readOnly={isLocked} />
                        <Feedback>{validation && validation.interpretation}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.original}</Label>
                        <Textarea value={original} onChange={setOriginal} readOnly={isLocked} />
                        <Feedback>{validation && validation.original}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.note}</Label>
                        <Textarea value={note} onChange={setNote} readOnly={isLocked} />
                        <Feedback>{validation && validation.note}</Feedback>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.parentParagraph}</Label>
                        <SelectDropdown title={parentHeading ? parentHeading.title : <Empty />}>
                            <SelectDropdownSearch
                                value={parentHeadingSearch}
                                onChange={(e) => dispatch(setCcLibraryHeadingAvailableParentSearch(e.target.value))}
                                onClearSearch={() => dispatch(setCcLibraryHeadingAvailableParentSearch(""))}
                            />
                            <SelectDropdownItem onClick={() => setParentHeading(null)}>
                                <Empty />
                            </SelectDropdownItem>
                            {
                                parentHeadingsFiltered.map((row) => (
                                    <SelectDropdownItem
                                        key={row.id}
                                        onClick={() => {
                                            setParentHeading(row)
                                            setParentItem(null)
                                        }}
                                    >
                                        {row.title}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.parentItem}</Label>
                        <SelectDropdown title={parentItem
                            ? `${parentItem.publicId} - ${parentItem.compliantItem}`
                            : <Empty />}
                        >
                            <SelectDropdownSearch
                                value={parentItemSearch}
                                onChange={(e) => dispatch(setCcLibraryItemAvailableParentSearch(e.target.value))}
                                onClearSearch={() => dispatch(setCcLibraryItemAvailableParentSearch(""))}
                            />
                            <SelectDropdownItem onClick={() => setParentItem(null)}>
                                <Empty />
                            </SelectDropdownItem>
                            {
                                parentItemsFiltered.map((row) => (
                                    <SelectDropdownItem
                                        key={row.id}
                                        onClick={() => {
                                            setParentItem(row)
                                            setParentHeading(null)
                                        }}
                                    >
                                        {`${row.publicId} - ${row.compliantItem}`}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    {
                        item.id && (
                            <FormGroup>
                                <Label>{lang.creator}</Label>
                                {
                                    !project.id && (
                                        item.accountId === userData.accountId
                                            ? <DivInput><UserIcon user={creator} size="sm" /></DivInput>
                                            : <Input type="text" value={ownerAccount ? ownerAccount.name : ""} readOnly />
                                    )
                                }
                                {
                                    project.id && (
                                        item.projectId === project.id
                                            ? <DivInput><UserIcon user={creator} size="sm" /></DivInput>
                                            : <Input type="text" value={ownerAccount ? ownerAccount.name : ""} readOnly />
                                    )
                                }
                            </FormGroup>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.answerType}</Label>
                        {
                            isLocked
                                ? <Input type="text" value={lang[answerType as keyof LangType]} readOnly />
                                : (
                                    <SelectDropdown title={lang[answerType as keyof LangType]}>
                                        <SelectDropdownItem onClick={() => setAnswerType(TEXT)}>{lang.text}</SelectDropdownItem>
                                        <SelectDropdownItem onClick={() => setAnswerType(YES_NO)}>{lang.yes_no}</SelectDropdownItem>
                                        <SelectDropdownItem onClick={() => setAnswerType(MULTIPLE)}>{lang.multiple}</SelectDropdownItem>
                                    </SelectDropdown>
                                )
                        }
                    </FormGroup>
                    {
                        answerType === MULTIPLE && (
                            <FormGroup>
                                <Label>{lang.options}</Label>
                                <Textarea value={options} onChange={setOptions} readOnly={isLocked} />
                                <Feedback>{validation && validation.options}</Feedback>
                            </FormGroup>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.group}</Label>
                        <Input type="text" value={group || ""} onChange={setGroup} readOnly={isLocked} />
                        <Feedback>{validation && validation.group}</Feedback>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
